/**
 * Metric keys whose comparison value is a percentage. TELV1-575.
 *
 * Shared because the statistical table is rendered TWICE — on screen in
 * ComparisonReport and again in the markdown download via
 * formatComparisonExport. Two copies of this list drift the moment a metric
 * is added, and the two surfaces would then disagree. PR review.
 *
 * Counts (completed_interviews, started_interviews) must stay bare, so this
 * is an explicit list rather than a default.
 */
export const PERCENTAGE_METRIC_KEYS = new Set(['completion_rate_pct', 'screening_pass_rate_pct']);

export const isPercentageMetric = (metricKey?: string) =>
  !!metricKey && PERCENTAGE_METRIC_KEYS.has(metricKey);

/**
 * Metric keys whose comparison value is a duration in MILLISECONDS.
 * TELV1-581.
 *
 * Only the `_ms` keys. The legacy `average_time_min` / `median_time_min` on
 * comparisons persisted before that change are rounded to whole minutes, so
 * they stay bare — formatting them as a duration would present a lossy value
 * as an exact one.
 */
export const DURATION_METRIC_KEYS = new Set(['average_time_ms', 'median_time_ms']);

export const isDurationMetric = (metricKey?: string) =>
  !!metricKey && DURATION_METRIC_KEYS.has(metricKey);

/**
 * The stable identifier for the completed-interviews row. TELV1-582.
 *
 * `metric_key` was added after the first comparisons shipped, so documents
 * persisted before it exist with only the English `metric` label. That label
 * is the ONLY way to find the row in those documents — and the sample-size
 * hero in ComparisonReport is gated on finding it, so losing the fallback
 * would blank the hero entirely rather than merely mislabel it.
 *
 * Do not "tidy up" the legacy string: TELV1-581 renamed a sibling English
 * label ('Average Time (min)' -> 'Average Time'), which is exactly the kind
 * of edit that would silently break a `metric`-based lookup.
 */
const COMPLETED_INTERVIEWS_METRIC_KEY = 'completed_interviews';
const LEGACY_COMPLETED_INTERVIEWS_METRIC = 'Completed Interviews';

export interface ComparisonSampleSizes {
  /** Respondents in segment A. */
  sampleA: number;
  /** Respondents in segment B. */
  sampleB: number;
  /**
   * Denominator for "N of M". 0 means "unknown" — callers should render the
   * bare count rather than invent a total.
   */
  totalSample: number;
  /** False when neither lookup found the completed-interviews row. */
  hasCompletedRow: boolean;
}

/**
 * Pull the sample-size numbers for the comparison hero out of a result.
 *
 * TELV1-582 — the denominator is the project-wide respondent total, NOT
 * `sampleA + sampleB`. Segments can overlap (one respondent may be in both)
 * or leave gaps, so the sum double-counts and reads to a researcher as the
 * size of the study. On a 25-respondent project this rendered "13 of 16",
 * directly above an AI narrative that correctly said "13 of 25 total".
 *
 * Extracted as a pure function so the four cases below are unit-testable
 * without mounting ComparisonReport (which pulls the router, search params
 * and a question query).
 */
export const resolveComparisonSampleSizes = (data: {
  statistical_comparison?: Array<{
    metric_key?: string;
    metric: string;
    filter_a_value: number;
    filter_b_value: number;
  }>;
  project_total_respondents?: number;
}): ComparisonSampleSizes => {
  const rows = data.statistical_comparison ?? [];
  const completedRow =
    rows.find((row) => row.metric_key === COMPLETED_INTERVIEWS_METRIC_KEY) ??
    rows.find((row) => row.metric === LEGACY_COMPLETED_INTERVIEWS_METRIC);

  return {
    sampleA: completedRow?.filter_a_value ?? 0,
    sampleB: completedRow?.filter_b_value ?? 0,
    totalSample: data.project_total_respondents ?? 0,
    hasCompletedRow: !!completedRow,
  };
};
